import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { MainHeader } from '../components/MainHeader';
import { HeaderPro } from '../components/HeaderPro';
import { Footer } from '../components/Footer';
import { PrimaryButton } from '../components/PrimaryButton';
import { getHelpArticle, type HelpArticle } from '../../lib/site-api';
import { toast } from '../../lib/toast';

type Props = {
  /**
   * Which portal the page is rendering inside. Drives the header chrome
   * and the audience gate sent to the public API — a pro-only article
   * loaded from the customer route should 404.
   */
  audience: 'CUSTOMER' | 'PROFESSIONAL';
};

// Shared by /help-center/article/:code and /professional/help-center/article/:code.
// The audience prop comes from the route definition, not the URL, so a customer
// can't request a PROFESSIONAL article by tweaking the path.
export default function HelpArticlePage({ audience }: Props) {
  const navigate = useNavigate();
  const params = useParams<{ code: string }>();
  const code = params?.code ?? '';

  const [article, setArticle] = useState<HelpArticle | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const helpCenterHref =
    audience === 'PROFESSIONAL' ? '/professional/help-center' : '/help-center';

  useEffect(() => {
    if (!code) return;
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    getHelpArticle(code, audience)
      .then((res) => {
        if (cancelled) return;
        if (res.status && res.info) {
          setArticle(res.info);
        } else {
          setNotFound(true);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        const status = err?.response?.status;
        if (status === 404 || status === 400) {
          setNotFound(true);
        } else {
          toast.error('Could not load this article right now.');
          setNotFound(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [code, audience]);

  const Header = audience === 'PROFESSIONAL' ? HeaderPro : MainHeader;

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />

      <main className="flex-1 pb-24">
        <section className="bg-[#f9f9f9] py-10 md:py-16 px-4 border-b border-[#ede8e9]">
          <div className="max-w-[820px] mx-auto">
            <button
              type="button"
              onClick={() => navigate(helpCenterHref)}
              className="inline-flex items-center gap-1 text-[#8e2a2f] font-['DM_Sans',sans-serif] font-bold text-[13px] mb-6 hover:underline"
            >
              <span className="material-icons text-[18px]">chevron_left</span>
              Back to Help Center
            </button>

            {loading ? (
              <div className="text-[#908e8e] py-8">Loading…</div>
            ) : notFound || !article ? (
              <div className="py-8">
                <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#3a3335] text-[28px] leading-[40px] mb-2">
                  Article not found
                </h1>
                <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px] leading-[21px]">
                  The article you're looking for may have been moved or
                  removed.
                </p>
              </div>
            ) : (
              <>
                <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#3a3335] text-[28px] md:text-[34px] leading-[40px] md:leading-[48px] tracking-[-0.094px] mb-4">
                  {article.title}
                </h1>
                {article.summary && (
                  <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[15px] leading-[24px] max-w-[680px]">
                    {article.summary}
                  </p>
                )}
              </>
            )}
          </div>
        </section>

        {!loading && !notFound && article && (
          <article className="max-w-[820px] mx-auto px-4 pt-10 md:pt-14">
            {article.cover_image_url && (
              <div className="mb-8 rounded-[8px] overflow-hidden border border-[#ede8e9]">
                <img
                  src={article.cover_image_url}
                  alt={article.title}
                  className="w-full h-auto block"
                />
              </div>
            )}

            <div
              className="help-article-body font-['DM_Sans',sans-serif] text-[#3a3335] text-[15px] leading-[26px]"
              dangerouslySetInnerHTML={{ __html: article.body }}
            />
          </article>
        )}

        <section className="max-w-[820px] mx-auto px-4 mt-16 py-8 flex flex-col md:flex-row md:items-center justify-between gap-6 border-t border-[#ede8e9]">
          <div>
            <h2 className="font-['Playfair_Display',sans-serif] font-bold text-[#3a3335] text-[16px] leading-[24px] mb-1">
              Can't find what you need?
            </h2>
            <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px] leading-[21px]">
              {audience === 'PROFESSIONAL'
                ? 'Our dedicated pro support team is here to help.'
                : "Send us a message and we'll get back to you."}
            </p>
          </div>
          <PrimaryButton
            onClick={() => navigate('/contact-us')}
            className="w-auto px-8 shrink-0 !h-[44px]"
          >
            {audience === 'PROFESSIONAL' ? 'Contact pro support' : 'Contact us'}
          </PrimaryButton>
        </section>
      </main>

      <Footer />
    </div>
  );
}
